import { matchesStreamSearchFilters } from '../streamSearchFilters'
import type {
	ListStreamSummariesResponse,
	ListStreamsResponse,
	SchemaUpdatesResponse,
	Stream,
	StreamField,
	StreamFilterOptionsPayload,
	StreamFilterOptionsResponse,
	StreamSearchFilters,
	StreamSearchPayload,
	StreamSearchResponse,
} from '../streamsApi'
import { demoApi } from './db'

export const streamsApi = {
	getStreams: async (page: number, limit: number, tenantId?: string): Promise<ListStreamsResponse> => {
		void tenantId
		return demoApi.get(`/v1/streams?page=${page}&limit=${limit}`)
	},

	// Demo dataset is small — fetch the full list and strip `fields` down to a count client-side.
	getStreamSummaries: async (page: number, limit: number, tenantId?: string): Promise<ListStreamSummariesResponse> => {
		void tenantId
		const { streams } = await demoApi.get<ListStreamsResponse>(`/v1/streams?page=${page}&limit=${limit}`)
		// An empty list can come back as `{}` rather than `[]` — coerce before mapping.
		const streamsList = Array.isArray(streams) ? streams : []
		return {
			streams: streamsList.map(stream => ({
				name: stream.name,
				dependency: stream.dependency,
				fields_count: stream.fields.length,
			})),
		}
	},
	// Demo dataset is small — fetch the full list and filter/sort/paginate client-side.
	searchStreams: async (
		payload: StreamSearchPayload,
		tenantId?: string,
		signal?: AbortSignal,
	): Promise<StreamSearchResponse> => {
		void tenantId
		void signal
		const { streams } = await demoApi.get<ListStreamsResponse>('/v1/streams?page=1&limit=1000')
		const streamsList = Array.isArray(streams) ? streams : []
		const matched = payload.filters
			? streamsList.filter(stream => matchesStreamSearchFilters(stream, payload.filters as StreamSearchFilters))
			: streamsList

		const sortBy = payload.sort_by ?? 'name'
		const order = payload.order ?? 'asc'
		const sorted = [...matched].sort((a, b) => {
			const cmp = sortBy === 'fields_count' ? a.fields.length - b.fields.length : a.name.localeCompare(b.name)
			return order === 'desc' ? -cmp : cmp
		})

		const start = (payload.page - 1) * payload.per_page
		const page = sorted.slice(start, start + payload.per_page)
		return {
			count: sorted.length,
			streams: page.map(stream => ({
				name: stream.name,
				fields_count: stream.fields.length,
				dependency: stream.dependency,
				...(payload.include_fields ? { fields: stream.fields } : {}),
			})),
		}
	},

	getStreamFilterOptions: async (
		payload: StreamFilterOptionsPayload,
		tenantId?: string,
		signal?: AbortSignal,
	): Promise<StreamFilterOptionsResponse> => {
		void tenantId
		void signal
		const { streams } = await demoApi.get<ListStreamsResponse>('/v1/streams?page=1&limit=1000')
		const streamsList = Array.isArray(streams) ? streams : []
		const filtered = payload.filters
			? streamsList.filter(stream => matchesStreamSearchFilters(stream, payload.filters as StreamSearchFilters))
			: streamsList
		const search = payload.search?.trim().toLowerCase() ?? ''
		const start = (payload.page - 1) * payload.per_page
		const column = payload.column

		if (column.kind === 'name') {
			const names = filtered
				.map(stream => stream.name)
				.filter(name => !search || name.toLowerCase().includes(search))
				.sort()
			return { column, count: names.length, values: names.slice(start, start + payload.per_page) }
		}

		const scoped = column.stream ? filtered.filter(stream => stream.name === column.stream) : filtered

		if (column.kind === 'field') {
			// Keyed by (name, type), not name alone — the same field name can carry different types across
			// streams (e.g. `latitude` as Int8 on one, Int16 on another), and those need to stay distinct
			// entries for the UI to club and disambiguate them, rather than collapsing to one arbitrary type.
			const seen = new Map<string, { name: string; type: string }>()
			for (const stream of scoped) {
				for (const field of stream.fields) {
					const key = `${field.name} ${field.type}`
					if (!seen.has(key)) {
						seen.set(key, { name: field.name, type: field.type })
					}
				}
			}
			const entries = [...seen.values()]
				.filter(({ name }) => !search || name.toLowerCase().includes(search))
				.sort((a, b) => a.name.localeCompare(b.name) || a.type.localeCompare(b.type))
			return {
				column,
				count: entries.length,
				values: entries.slice(start, start + payload.per_page),
			}
		}

		// 'field_type'
		const types = new Set<string>()
		for (const stream of scoped) {
			for (const field of stream.fields) {
				types.add(field.type)
			}
		}
		const values = [...types].filter(type => !search || type.toLowerCase().includes(search)).sort()
		return { column, count: values.length, values: values.slice(start, start + payload.per_page) }
	},

	getStream: async (streamName: string, tenantId?: string): Promise<Stream> => {
		void tenantId
		const res = await demoApi.get<{
			result: Record<string, { type: string; seq_id: number; required: string }>
		}>(`/v1/streams/${encodeURIComponent(streamName)}/fields`)

		const fields: StreamField[] = Object.entries(res.result).map(([name, detail]) => ({
			name,
			type: detail.type,
			formatter: null,
		}))

		return { name: streamName, fields }
	},

	createStream: async (
		streamName: string,
		fields: StreamField[],
		tenantId?: string,
	): Promise<{ status: string; name: string }> => {
		void tenantId
		const v1Fields: Record<string, Record<string, unknown>> = {}
		fields.forEach((f, index) => {
			v1Fields[f.name] = {
				type: f.type,
				required: false,
				seq_id: index,
				unit: f.formatter ?? null,
			}
		})

		await demoApi.post('/v1/streams', { streamName, fields: v1Fields })
		return { status: 'success', name: streamName }
	},

	createStreamField: async (
		streamName: string,
		fieldName: string,
		fieldType: string,
		formatter: string | null,
		tenantId?: string,
	): Promise<{ stream_name: string; field_name: string }> => {
		void tenantId
		await demoApi.post('/v1/streams/fields', {
			streamName,
			fieldName,
			fieldType,
			fieldUnit: formatter ?? null,
		})
		return { stream_name: streamName, field_name: fieldName }
	},

	/**
	 * Update stream field (V1 API)
	 * POST /v1/streams/fields/update
	 * TODO: Demo API currently only supports updating unit (formatter). Need to support both field type and unit changes in the same API call like doing in V3
	 */
	updateStreamField: async (
		streamName: string,
		fieldName: string,
		updates: { type?: string; formatter?: string | null },
		tenantId?: string,
	): Promise<{ status: string }> => {
		void tenantId
		await demoApi.post('/v1/streams/fields/update', {
			streamName,
			fieldName,
			fieldType: updates.type ?? null,
			fieldUnit: updates.formatter ?? null,
		})
		return { status: 'success' }
	},

	deleteStreamField: async (streamName: string, fieldName: string, tenantId?: string): Promise<{ status: string }> => {
		void tenantId
		await demoApi.delete(`/v1/streams/${encodeURIComponent(streamName)}/fields/${encodeURIComponent(fieldName)}`)
		return { status: 'success' }
	},

	deleteStream: async (streamName: string, tenantId?: string): Promise<{ status: string }> => {
		void tenantId
		await demoApi.delete(`/v1/streams/${encodeURIComponent(streamName)}`)
		return { status: 'success' }
	},

	getSchemaUpdates: async (_active?: boolean, _page = 1, _perPage = 20): Promise<SchemaUpdatesResponse> => {
		return { count: 0, data: [] }
	},
}
